import { useCallback, useEffect, useState } from 'react';
import { SectionVersementIdentite } from './versement-identite';
import { api, ErreurApi, type ActionDossier, type Dossier, type DossierDePieces,
  type EditionsFicheParties, type EtatFicheParties } from './api';
// §23 · §24 · §36 · §37 — ce qui bloque, ce qu'il reste à faire, et les versions.
import { Diligences } from './diligences';
// ⚠️ SectionProjection vivait ICI, en local. Extraite le 24/09/2026 : la fiche
// parcelle la sert aussi, et deux copies d'une même règle divergent au premier
// correctif appliqué à une seule.
import { SectionProjection } from './projection';

/**
 * LE DOSSIER D'IMMATRICULATION (§31, §34) — de la demande à la remise du titre.
 *
 * ⚠️ CET ÉCRAN N'EXISTAIT PAS, ET LA CHAÎNE ENTIÈRE ÉTAIT INJOUABLE SANS CURL.
 * Cinq routes d'écriture — ouvrir la publicité, inscrire une opposition, la
 * clore, prononcer l'immatriculation, éditer le certificat — n'avaient AUCUN
 * écrivain côté interface. Elles avaient été éprouvées en production par appels
 * directs, ce qui prouve le service et ne délivre aucun titre à personne : un
 * acte que seul un ingénieur peut poser n'est pas un acte de l'administration.
 *
 * ⚠️ AUCUNE RÈGLE MÉTIER N'EST RECOPIÉE ICI. Ce que l'on peut faire, et
 * pourquoi on ne le peut pas, vient de `dossier.actions` — décidé par le
 * service, au même endroit que le refus. Un écran qui déciderait lui-même
 * afficherait tôt ou tard un bouton ouvert qui rend 409, ou fermé sans raison.
 *
 * ⚠️ UN BOUTON FERMÉ EST AFFICHÉ, DÉSACTIVÉ ET MOTIVÉ — jamais absent. Absent,
 * l'agent conclut que la fonction n'existe pas et cherche ailleurs ; désactivé
 * avec son motif, il sait ce qu'il lui manque. C'est la même règle que le rail
 * des rubriques.
 */

/**
 * LES ONGLETS DE LA FICHE — §77 « Mutation 360° », niveau 3 de la navigation.
 *
 * ⚠️ POURQUOI DES ONGLETS PLUTÔT QUE HUIT SECTIONS EMPILÉES. Mesuré le
 * 21/09/2026 : cette fiche empile 8 sections sur 688 lignes. L'agent qui vient
 * vérifier une pièce déroule toute l'instruction, la publicité et les
 * oppositions avant de l'atteindre — et déroule à nouveau pour revenir. Le
 * cahier du fondateur le dit à son §77 : la fiche porte des onglets.
 *
 * ⚠️⚠️ CE QUI NE VA JAMAIS DANS UN ONGLET : LES BLOCAGES. Ils restent
 * AU-DESSUS, visibles quel que soit l'onglet ouvert. Un empêchement rangé
 * derrière un onglet est un empêchement qu'on découvre après avoir travaillé —
 * c'est la raison pour laquelle ils étaient en tête de la version empilée, et
 * la raison ne change pas parce que la navigation change.
 */
type OngletDossier = 'resume' | 'effet' | 'parties' | 'pieces' | 'acte' | 'publicite';

const ONGLETS: Array<{ cle: OngletDossier; libelle: string }> = [
  { cle: 'resume',    libelle: 'Résumé' },
  { cle: 'effet',     libelle: 'Effet sur le registre' },
  { cle: 'parties',   libelle: 'Parties' },
  { cle: 'pieces',    libelle: 'Pièces' },
  { cle: 'acte',      libelle: 'Acte et titre' },
  { cle: 'publicite', libelle: 'Publicité' },
];

const ETIQUETTE: Record<string, string> = {
  deposee: 'ambre', instruction: 'ambre', controle_technique: 'ambre',
  attente_validation: 'bleu', validee: 'vert', rejetee: 'rouge', annulee: 'gris',
};

function Bouton({ action, onClic, primaire }: {
  action: ActionDossier | undefined; onClic: () => void; primaire?: boolean;
}) {
  if (!action) return null;
  return (
    <button
      className={`bouton ${primaire ? 'primaire' : 'discret'}`}
      disabled={!action.ouverte}
      onClick={onClic}
      title={action.ouverte ? action.libelle : action.motif}
    >
      {action.libelle}
    </button>
  );
}

export function PanneauDossier({ reference, onFerme, onChange }: {
  reference: string; onFerme: () => void; onChange: () => void;
}) {
  const [d, setD] = useState<Dossier | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [formulaire, setFormulaire] = useState<'publicite' | 'opposition' | null>(null);
  const [onglet, setOnglet] = useState<OngletDossier>('resume');
  const [certificat, setCertificat] = useState<{ document: string; url: string } | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setD(await api.mutations.affaire(reference)); }
    catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter ce dossier (fon.mutation:lire)."
        : String((e as Error).message ?? e));
    }
  }, [reference]);

  useEffect(() => { void charger(); }, [charger]);

  /** ⚠️ Tout geste recharge le dossier : les blocages changent à chaque acte,
   *  et un panneau qui garderait son état d'avant proposerait le geste suivant
   *  sur une situation périmée. */
  async function agir(f: () => Promise<{ message?: string }>) {
    setOccupe(true); setErreur(null); setMessage(null);
    try {
      const r = await f();
      setMessage(r.message ?? 'Enregistré.');
      await charger();
      onChange();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  const action = (code: ActionDossier['code']) => d?.actions.find((a) => a.code === code);

  async function prononcer() {
    const motif = window.prompt(
      `Prononcer l'immatriculation de ${reference}.\n\n`
      + `Cet acte DÉLIVRE un titre foncier et engage l'État. Motif consigné au journal :`);
    if (motif === null) return;
    if (motif.trim().length < 3) { setErreur('Un motif est obligatoire pour prononcer une immatriculation.'); return; }
    await agir(() => api.mutations.immatriculer(reference, motif.trim()));
  }

  /**
   * ⚠️ MOTIF DE 20 CARACTÈRES, ET CE N'EST PAS DE LA SÉVÉRITÉ GRATUITE. Ce
   * geste délivre un titre dont la date d'émission sera POSTÉRIEURE à
   * l'immatriculation qu'il constate. Ce qu'on écrit ici est ce qu'un
   * contrôleur lira pour comprendre l'écart — « ok » ne l'expliquerait pas.
   */
  async function regulariser() {
    const motif = window.prompt(
      `Régulariser le titre de ${reference}.\n\n`
      + `Cette immatriculation a été prononcée sans délivrer de titre. Ce geste en émet un `
      + `aujourd'hui et ouvre la feuille au livre foncier : sa date d'émission sera postérieure `
      + `à l'acte qu'il constate.\n\nMotif (20 caractères minimum, consigné au journal) :`);
    if (motif === null) return;
    if (motif.trim().length < 20) {
      setErreur("Un motif d'au moins 20 caractères est exigé : ce titre portera une date postérieure à son acte.");
      return;
    }
    await agir(() => api.mutations.regulariserTitre(reference, motif.trim()));
  }

  async function clore(ref: string, suite: 'retenue' | 'ecartee') {
    const motif = window.prompt(
      suite === 'retenue'
        ? `RETENIR l'opposition ${ref} — la demande ne pourra plus aboutir en l'état.\nMotif (10 caractères minimum) :`
        : `ÉCARTER l'opposition ${ref} — elle cessera de bloquer l'immatriculation.\nMotif (10 caractères minimum) :`);
    if (motif === null) return;
    if (motif.trim().length < 10) { setErreur('Une clôture sans motif suffisant est refusée par le registre.'); return; }
    await agir(() => api.oppositions.clore(ref, { suite, motif: motif.trim() }));
  }

  /**
   * ⚠️ DEUX CERTIFICATS, DEUX AUTORITÉS, UN SEUL GESTE À L'ÉCRAN. Le titre
   * foncier est édité par la Conservation, le certificat de donation par la
   * commune. Ce qui change n'est ni le bouton ni la mise en page : c'est la
   * route, parce que l'autorité qui signe n'est pas la même. Le service
   * vérifie l'organisation de l'appelant dans les deux cas.
   */
  async function editer(quoi: 'titre' | 'acte') {
    if (quoi === 'titre' && !d?.titre) return;
    setOccupe(true); setErreur(null); setMessage(null);
    try {
      const r = quoi === 'titre'
        ? await api.documents.editerCertificat(d!.titre!.identifiant, {})
        : await api.documents.editerCertificatActe(reference, {});
      setCertificat({ document: r.document, url: r.urlTelechargement });
      setMessage(r.message);
      onChange();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error).message ?? e));
    } finally { setOccupe(false); }
  }

  if (erreur && !d) return (
    <div className="fiche-formulaire large">
      <p className="erreur" role="alert">{erreur}</p>
      <button className="bouton discret" onClick={onFerme}>Fermer</button>
    </div>
  );
  if (!d) return <div className="attente-plein">Chargement du dossier {reference}…</div>;

  return (
    <div className="fiche-formulaire large dossier">
      <header className="entete-scene">
        <h3>Dossier <code>{d.mutation.reference}</code> — {d.mutation.type}</h3>
        <div className="actions">
          <span className={`etat ${ETIQUETTE[d.mutation.statut] ?? 'gris'}`}>{d.mutation.statut}</span>
          <button className="bouton discret" onClick={onFerme}>Fermer</button>
        </div>
      </header>

      {d.blocs_masques.length > 0 && (
        <p className="note-indispo">
          Votre habilitation ne couvre pas : {d.blocs_masques.join(', ')}. Ces parties du dossier
          existent peut-être — elles ne vous sont pas montrées. Les blocages restent comptés.
        </p>
      )}

      {message && <p className="succes" role="status">{message}</p>}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {/* ─── Ce qui bloque, en tête : c'est la question que l'agent se pose ─── */}
      {d.blocages.length > 0 ? (
        <div className="avertissement danger">
          <strong>L'immatriculation ne peut pas être prononcée :</strong>
          <ul>{d.blocages.map((b, k) => <li key={k}>{b}</li>)}</ul>
        </div>
      ) : d.mutation.statut !== 'validee' && d.mutation.type === 'immatriculation' ? (
        <div className="avertissement">
          <strong>Rien ne s'oppose plus à l'immatriculation.</strong> Prononcer délivrera
          un titre foncier et inscrira les droits — il n'y a pas d'étape ultérieure.
        </div>
      ) : null}

      {/**
        * ⚠️ LA BARRE EST APRÈS LES BLOCAGES, JAMAIS AVANT. Ce qui empêche
        * l'acte reste visible quel que soit l'onglet ouvert : un empêchement
        * rangé derrière un onglet se découvre APRÈS avoir travaillé, et c'est
        * la raison pour laquelle il était en tête de la version empilée.
        */}
      <nav className="onglets">
        {ONGLETS.map((o) => (
          <button key={o.cle} type="button"
            className={`bouton ${onglet === o.cle ? 'actif' : 'discret'}`}
            onClick={() => setOnglet(o.cle)}>{o.libelle}</button>
        ))}
      </nav>

      {onglet === 'effet' && <SectionProjection reference={reference} />}

{onglet === 'resume' && (
      <section>
        <h4>Instruction</h4>
        <div className="ligne-fiche">
          <span>Parcelle</span>
          <strong>{d.mutation.parcelle ? <code>{d.mutation.parcelle}</code> : '—'}</strong>
        </div>
        <div className="ligne-fiche">
          <span>Approbations</span>
          <strong>{d.approbationsDistinctes}/2</strong>
        </div>
        {d.approbations.length > 0 && (
          <ul className="liste-fine">
            {d.approbations.map((a, k) => (
              <li key={k}>
                {a.decision === 'approuve' ? '✓' : '✗'} {a.autorite_nom ?? 'organisation inconnue'}
                {a.autorite_type ? ` (${a.autorite_type})` : ''} — {new Date(a.decide_le).toLocaleDateString('fr-FR')}
                {a.commentaire ? ` · ${a.commentaire}` : ''}
              </li>
            ))}
          </ul>
        )}
        {d.avisManquants.length > 0 && (
          <p className="note-indispo">Avis encore attendus : {d.avisManquants.join(', ')}.</p>
        )}
      </section>
      )}

      {/* ⚠️ AVANT la publicité, et ce n'est pas un rangement : la publicité
          convoque les tiers contre un acte dont les PARTIES sont censées être
          identifiées. Placer l'identification après reviendrait à publier
          d'abord, à vérifier qui signe ensuite. */}
      {onglet === 'parties' && <SectionFicheParties reference={reference} />}

      {/* ⚠️ SOUS L'ONGLET DES PARTIES, et c'est le seul endroit juste : ce
          dispositif ÉTABLIT l'identité d'un intervenant. Le ranger sous
          « pièces » le ferait lire comme un dépôt de fichier parmi d'autres,
          alors qu'il tranche qui est partie à l'acte.
          ⚠️ ET IL VAUT POUR LES 10 TYPES DE DEMANDE ET LES 11 OPÉRATIONS sans
          en connaître aucun : il prend une affaire et un titulaire. Le câbler
          par type est ce qui a rendu 500 sur 8 affaires sur 19 le 22/09. */}
      {onglet === 'parties' && (
        <SectionVersementIdentite reference={reference} onChange={onChange} />
      )}

      {/* ⚠️ APRÈS l'identification des parties et AVANT la publicité, et c'est
          un ordre, pas un rangement. La publicité convoque des tiers contre cet
          acte : on doit savoir AVANT quelles pièces le corroborent, et
          lesquelles manquent. Publier d'abord et constater ensuite qu'aucun
          plan n'a jamais été délivré, c'est convoquer sur du vide. */}
      {onglet === 'pieces' && <SectionDossierDePieces reference={reference} />}

{onglet === 'publicite' && (
      <section>
        <h4>Publicité foncière</h4>
        {d.publicite ? (
          <>
            <div className="ligne-fiche"><span>Support</span><strong>{d.publicite.support}</strong></div>
            <div className="ligne-fiche">
              <span>Délai d'opposition</span>
              <strong>
                {d.publicite.delai_jours} jours — {d.publicite.echue
                  ? `clos depuis le ${d.publicite.echeance}`
                  : `court jusqu'au ${d.publicite.echeance}`}
              </strong>
            </div>
          </>
        ) : (
          <p className="vide">Aucune publicité ouverte : le délai d'opposition n'a pas commencé.</p>
        )}
        <div className="actions">
          <Bouton action={action('publicite')} onClic={() => setFormulaire('publicite')} />
        </div>
      </section>
      )}

{onglet === 'publicite' && (
      <section>
        <h4>Oppositions</h4>
        {d.oppositions.length === 0 ? (
          <p className="vide">Aucune opposition inscrite sur ce dossier.</p>
        ) : (
          <table className="tableau">
            <thead><tr><th>Référence</th><th>Opposant</th><th>Objet</th><th>État</th><th /></tr></thead>
            <tbody>
              {d.oppositions.map((o) => (
                <tr key={o.reference}>
                  <td><code>{o.reference}</code></td>
                  <td>{o.opposant_nom}</td>
                  <td>{o.objet}</td>
                  <td>
                    <span className={`etat ${o.bloquante ? 'rouge' : 'vert'}`}>
                      {o.statut === 'clos' ? `close · ${o.decision ?? 'sans décision'}` : 'ouverte'}
                    </span>
                  </td>
                  <td className="actions-ligne">
                    {o.statut !== 'clos' && (
                      <>
                        <button className="bouton discret" disabled={occupe}
                                onClick={() => void clore(o.reference, 'ecartee')}>Écarter</button>
                        <button className="bouton discret danger" disabled={occupe}
                                onClick={() => void clore(o.reference, 'retenue')}>Retenir</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="actions">
          <Bouton action={action('opposition')} onClic={() => setFormulaire('opposition')} />
        </div>
      </section>
      )}

      {/* ⚠️ UNE MUTATION QUI N'EST PAS UNE IMMATRICULATION NE DÉLIVRE AUCUN
          TITRE — et afficher « aucun titre délivré » sur une donation ferait
          croire à un dossier incomplet. C'est son ACTE qui se certifie. */}
      {onglet === 'acte' && d.mutation.type !== 'immatriculation' && (
        <section>
          <h4>Acte</h4>
          <div className="ligne-fiche">
            <span>Nature</span><strong>{d.mutation.type}</strong>
          </div>
          <p className="vide">
            Un acte de ce type ne délivre pas de titre foncier : il transfère ou éteint des
            droits sur un immeuble déjà immatriculé. C'est son certificat qui s'édite.
          </p>
          <div className="actions">
            <Bouton action={action('certificat_acte')} onClic={() => void editer('acte')} primaire />
          </div>
        </section>
      )}

      {onglet === 'acte' && d.mutation.type === 'immatriculation' && (
      <section>
        <h4>Titre foncier</h4>
        {d.titre ? (
          <>
            <div className="ligne-fiche">
              <span>Titre délivré</span>
              <strong><code>{d.titre.identifiant}</code> — {d.titre.date_emission}</strong>
            </div>
            {certificat && (
              <p className="succes">
                Certificat <code>{certificat.document}</code> édité.{' '}
                {/* ⚠️ `target=_blank` et non un téléchargement : le PDF s'ouvre
                    dans le lecteur du navigateur, d'où l'impression est à un
                    clic. C'est ce que « remettre un titre » veut dire. */}
                <a href={certificat.url} target="_blank" rel="noreferrer">Ouvrir et imprimer</a>
              </p>
            )}
          </>
        ) : (
          <>
            <p className="vide">Aucun titre n'a encore été délivré sur ce dossier.</p>
            {/* ⚠️ « PAS ENCORE DÉLIVRÉ » ET « JAMAIS DÉLIVRÉ ALORS QUE L'ACTE EST
                FAIT » SONT DEUX ÉTATS, et les afficher pareil laisse le second
                invisible. Une immatriculation appliquée sans titre n'attend pas :
                elle manque. Le service décide ; l'écran le NOMME. */}
            {d.mutation.statut === 'validee' && d.mutation.applique_le !== null
             && d.mutation.type === 'immatriculation' && (
              <p className="avertissement danger">
                <strong>Cette immatriculation est appliquée et ne porte AUCUN titre.</strong>{' '}
                Les droits sont inscrits au registre ; le titre qui les constate n'a jamais été
                délivré. Tant qu'il manque, l'immeuble n'est opposable par aucun papier.
              </p>
            )}
          </>
        )}
        <div className="actions">
          <Bouton action={action('immatriculer')} onClic={() => void prononcer()} primaire />
          <Bouton action={action('regulariser_titre')} onClic={() => void regulariser()} />
          <Bouton action={action('certificat')} onClic={() => void editer('titre')} />
        </div>
      </section>
      )}

      {formulaire === 'publicite' && (
        <FormulairePublicite
          reference={reference}
          onFerme={() => setFormulaire(null)}
          onFait={async (corps) => {
            setFormulaire(null);
            await agir(() => api.mutations.ouvrirPublicite(reference, corps));
          }}
        />
      )}
      {formulaire === 'opposition' && (
        <FormulaireOpposition
          reference={reference}
          onFerme={() => setFormulaire(null)}
          onFait={async (corps) => {
            setFormulaire(null);
            await agir(() => api.mutations.inscrireOpposition(reference, corps));
          }}
        />
      )}
    </div>
  );
}

/**
 * §7 — LA FICHE D'IDENTIFICATION DES PARTIES, DEPUIS LE DOSSIER.
 *
 * ⚠️ ELLE N'AFFIRME JAMAIS UN RAPPROCHEMENT — elle le propose, en disant
 * comment. Mesuré au 15/09 : rien, dans le registre, ne relie une partie
 * déclarée au formulaire à un titulaire inscrit ; le seul pont restant est LE
 * NOM, et un nom n'identifie pas une personne. La mention « rapproché sur le
 * seul nom » s'imprime EN FACE du nom — jamais en note de bas de page, où elle
 * ne serait pas lue par celui qui signe.
 *
 * ⚠️ TROIS VERDICTS, JAMAIS DEUX. Avec une seule pièce d'identité déposée pour
 * cent titulaires, un binaire concordant/divergent afficherait « aucune
 * divergence » sur la totalité du pays : le vert obtenu faute d'avoir quoi
 * contredire. `nonEvaluables` a donc sa propre place à l'écran.
 */
function SectionFicheParties({ reference }: { reference: string }) {
  const [etat, setEtat] = useState<EtatFicheParties | null>(null);
  const [editions, setEditions] = useState<EditionsFicheParties | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setEtat(null); setEditions(null); setErreur(null);
    Promise.allSettled([
      api.mutations.ficheParties.etat(reference),
      api.mutations.ficheParties.editions(reference),
    ]).then(([e, v]) => {
      if (e.status === 'fulfilled') setEtat(e.value);
      else setErreur(String((e.reason as Error)?.message ?? e.reason));
      if (v.status === 'fulfilled') setEditions(v.value);
    });
  }, [reference]);

  const sansMoteur = etat !== null && !etat.rendudisponible;
  const bloque = etat !== null && (!etat.editable || sansMoteur);

  return (
    <section className="piece-fiche-parties">
      <h4>Identification des parties</h4>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!etat && !erreur && <p className="attente">Lecture des parties…</p>}

      {etat && (
        <>
          {/* ⚠️ « AUCUN FORMULAIRE » N'EST PAS « AUCUNE PARTIE ». Le premier dit
              que le dossier n'a rien à lire ; le second, qu'aucune autorité n'a
              défini qui sont les parties de ce type d'acte. Deux causes, deux
              suites — les afficher pareil enverrait corriger le mauvais bout. */}
          {etat.sansFormulaire && (
            <p className="note-indispo">
              Ce dossier ne porte aucun formulaire exploitable : les parties ne peuvent
              pas être lues, donc rien ne peut être confronté au registre.
            </p>
          )}

          <p className="mention">{etat.etat.lecture}</p>

          <ul className="fiche-comptes">
            <li>Concordantes : <strong>{etat.etat.concordantes}</strong>
              {' · '}Divergentes : <strong>{etat.etat.divergentes}</strong>
              {' · '}Non confrontables : <strong>{etat.etat.nonEvaluables}</strong></li>
            <li>Parties : {etat.etat.partiesEtablies} établie(s) ·
              {' '}{etat.etat.partiesDeclarees} déclarée(s) ·
              {' '}{etat.etat.partiesInconnues} inconnue(s)</li>
            {etat.etat.rapprochementsParLeNom > 0 && (
              <li className="alerte-trace">
                ⚠ {etat.etat.rapprochementsParLeNom} partie(s) rapprochée(s) SUR LE SEUL NOM.
              </li>
            )}
          </ul>

          {etat.parties.length > 0 && (
            <ul className="liste-parties">
              {etat.parties.map((p) => (
                <li key={p.cle} data-niveau={p.niveau} data-rattachement={p.rattachePar}>
                  <strong>{p.nom}</strong> <span className="mention">— {p.libelle}</span>
                  {' '}
                  <span className={`etiquette ${p.niveau === 'etabli' ? 'vert'
                    : p.niveau === 'declare' ? 'ambre' : 'gris'}`}>
                    {p.niveau === 'etabli' ? 'identité établie'
                      : p.niveau === 'declare' ? 'déclarée' : 'inconnue'}
                  </span>
                  {/* ⚠️ EN FACE DU NOM, pas en bas de page. */}
                  {p.rattachePar === 'nom' && (
                    <><br /><span className="alerte-trace">
                      ⚠ Rapprochée du registre sur le seul nom — un nom n'identifie pas une personne.
                    </span></>
                  )}
                  {p.rattachePar === 'aucun' && (
                    <><br /><span className="mention">
                      Aucun titulaire du registre ne lui est rattaché.
                    </span></>
                  )}
                  {p.confrontations.length > 0 && (
                    <ul className="confrontations">
                      {p.confrontations.map((c, k) => (
                        <li key={`${p.cle}-${k}`} data-verdict={c.verdict}>
                          <strong>{c.element}</strong> — déclaré « {c.declare} » /
                          {' '}registre « {c.registre} »
                          {' '}<span className={`etiquette ${c.verdict === 'concordant' ? 'vert'
                            : c.verdict === 'divergent' ? 'rouge' : 'gris'}`}>{c.verdict}</span>
                          {/* ⚠️ LE POURQUOI, JAMAIS SEULEMENT LE VERDICT : c'est
                              ce qui rend le jugement rejouable par un tiers. */}
                          <br /><span className="mention">{c.pourquoi}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}

          {etat.manques.length > 0 && (
            <>
              <p className="mention">Cette fiche ne pourra PAS établir :</p>
              <ul className="motifs">{etat.manques.map((m) => <li key={m}>{m}</li>)}</ul>
            </>
          )}
          {etat.reserves.length > 0 && (
            <ul className="reserves">{etat.reserves.map((r) => <li key={r}>{r}</li>)}</ul>
          )}

          {!etat.editable && etat.motifNonEditable && (
            <p className="erreur" role="alert">{etat.motifNonEditable}</p>
          )}
          {etat.editable && sansMoteur && (
            <p className="erreur" role="alert">
              Le moteur d'actes n'est pas configuré : cette fiche ne peut pas être délivrée.
              Son contenu reste lisible ci-dessus, sans valeur de document.
            </p>
          )}

          <a className={`bouton primaire${bloque ? ' desactive' : ''}`}
             href={bloque ? undefined : api.mutations.ficheParties.url(reference)}
             target="_blank" rel="noreferrer" aria-disabled={bloque}
             onClick={(e) => { if (bloque) e.preventDefault(); }}>
            Délivrer la fiche d'identification
          </a>
        </>
      )}

      {editions && editions.editions.length === 0 && (
        <p className="vide">Aucune fiche d'identification délivrée pour ce dossier.</p>
      )}
      {editions && editions.editions.length > 0 && (
        <ul className="liste-editions">
          {editions.editions.map((e) => (
            <li key={e.version} data-trace={e.piecesDeposeesDepuis > 0 ? 'perime' : 'a-jour'}>
              <strong>V{e.version}</strong> · {e.concordantes} concordante(s),
              {' '}{e.divergentes} divergente(s), {e.nonEvaluables} non confrontable(s)
              <br />
              <span className="mention">
                {new Date(e.editeLe).toLocaleString('fr-FR')} — {e.document}
              </span>
              {/* ⚠️ UNE BONNE NOUVELLE QUI PÉRIME LE PAPIER : des pièces déposées
                  depuis veulent dire que la fiche en circulation annonce encore
                  « non confrontable » là où le registre sait désormais. */}
              {e.piecesDeposeesDepuis > 0 && (
                <>
                  <br />
                  <span className="alerte-trace">
                    ⚠ {e.piecesDeposeesDepuis} pièce(s) d'identité déposée(s) depuis cette
                    édition : ce papier dit « non confrontable » là où le registre sait
                    désormais quelque chose.
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * §10 — LE DOSSIER DE PIÈCES : les huit pièces de l'acte, et l'état de chacune.
 *
 * ⚠️ LE BUT EST LA CONFRONTATION (D.615, demande du fondateur). Un plan qui dit
 * 340 m², un document d'arpentage qui dit 5 000 m² et un PV qui nomme quatre
 * riverains pour zéro parcelle contiguë ne se contredisent QUE si on les met
 * côte à côte. Tant que chaque pièce se délivre seule, personne ne le fait.
 *
 * ⚠️ AUCUN BOUTON DE DÉLIVRANCE ICI, ET C'EST DÉLIBÉRÉ. Cette section constate ;
 * elle ne délivre pas. Chaque pièce a son propre écran, avec ses propres
 * conditions — un bouton posé ici les contournerait, ou pire, promettrait une
 * pièce que la route refuse.
 *
 * ⚠️ TROIS ÉTATS, TROIS TRAITEMENTS VISUELS DISTINCTS, et jamais la couleur
 * seule : chaque état porte son MOT. Ces dossiers se lisent aussi imprimés, et
 * un agent daltonien lit le même écran que les autres.
 */
function SectionDossierDePieces({ reference }: { reference: string }) {
  const [d, setD] = useState<DossierDePieces | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setD(null); setErreur(null);
    api.mutations.pieces(reference)
      .then(setD)
      .catch((e) => setErreur(String((e as Error)?.message ?? e)));
  }, [reference]);

  return (
    <section className="piece-dossier-pieces">
      <Diligences reference={reference} />

      <h4>Pièces du dossier</h4>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!d && !erreur && <p className="attente">Lecture des pièces…</p>}

      {d && (
        <>
          {/* ⚠️ LA PHRASE DIT LA POPULATION, pas seulement le compte. « 6 sur 8 »
              ne dit pas si les deux manquantes sont délivrables. */}
          <p className="mention">{d.lecture}</p>

          <ul className="liste-pieces">
            {d.pieces.map((p) => (
              <li key={p.code} data-etat={p.etat}>
                <span className="mention">{p.section}</span>{' '}
                <strong>{p.libelle}</strong>{' '}
                <span className={`etiquette ${p.etat === 'delivree' ? 'vert'
                  : p.etat === 'absente' ? 'ambre' : 'gris'}`}>
                  {p.etat === 'delivree' ? 'délivrée'
                    : p.etat === 'absente' ? 'jamais délivrée' : 'non prévue'}
                </span>
                {p.etat === 'delivree' && (
                  <>
                    <br />
                    <span className="mention">
                      V{p.version} · {p.document}
                      {p.deliveeLe ? ` · ${new Date(p.deliveeLe).toLocaleString('fr-FR')}` : ''}
                    </span>
                  </>
                )}
                {/* ⚠️ LE POURQUOI, JAMAIS LE SEUL VERDICT : une raison qui ne dit
                    pas quoi faire ensuite est une impasse pour l'agent. */}
                {p.pourquoi && (<><br /><span className="mention">{p.pourquoi}</span></>)}
              </li>
            ))}
          </ul>

          {d.nonPrevues > 0 && (
            <div className="note">
              {d.nonPrevues} pièce(s) que le registre ne sait pas encore produire : ce n'est
              pas un oubli d'instruction, c'est un développement qui n'existe pas. Aucun
              bouton ne les délivrera.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function FormulairePublicite({ reference, onFerme, onFait }: {
  reference: string;
  onFerme: () => void;
  onFait: (c: { support: string; delaiJours: number }) => void;
}) {
  const [support, setSupport] = useState('');
  const [delai, setDelai] = useState(30);
  return (
    <form className="fiche-formulaire" onSubmit={(e) => { e.preventDefault(); onFait({ support: support.trim(), delaiJours: delai }); }}>
      <h4>Ouvrir la publicité foncière — {reference}</h4>
      {/* ⚠️ LE SUPPORT EST OBLIGATOIRE CÔTÉ SERVICE, et ce n'est pas une
          formalité : un délai que personne n'a pu voir courir ne s'oppose à
          personne. Le registre doit pouvoir dire OÙ la publicité a été faite. */}
      <label>Support de publicité <span className="aide">où le public a pu en prendre connaissance</span>
        <input value={support} onChange={(e) => setSupport(e.target.value)} required minLength={5}
               placeholder="ex. affichage mairie de Kaloum + radio rurale, 5 min minimum" />
      </label>
      <label>Délai d'opposition (jours)
        <input type="number" min={1} max={365} value={delai}
               onChange={(e) => setDelai(Number(e.target.value))} required />
      </label>
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire" disabled={support.trim().length < 5}>Ouvrir le délai</button>
      </div>
    </form>
  );
}

function FormulaireOpposition({ reference, onFerme, onFait }: {
  reference: string;
  onFerme: () => void;
  onFait: (c: { objet: string; opposantNom: string }) => void;
}) {
  const [nom, setNom] = useState('');
  const [objet, setObjet] = useState('');
  return (
    <form className="fiche-formulaire" onSubmit={(e) => { e.preventDefault(); onFait({ opposantNom: nom.trim(), objet: objet.trim() }); }}>
      <h4>Inscrire une opposition — {reference}</h4>
      {/* ⚠️ L'OPPOSANT N'EST PAS FORCÉMENT TITULAIRE — c'est même le cas
          ordinaire : il conteste précisément de ne pas l'être. On demande donc
          un NOM, jamais un identifiant de titulaire existant. */}
      <label>Opposant <span className="aide">celui qui s'oppose n'est pas forcément inscrit au registre</span>
        <input value={nom} onChange={(e) => setNom(e.target.value)} required minLength={2} />
      </label>
      <label>Objet de l'opposition <span className="aide">10 caractères minimum, consigné au registre</span>
        <textarea value={objet} onChange={(e) => setObjet(e.target.value)} rows={3} required minLength={10} />
      </label>
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire" disabled={nom.trim().length < 2 || objet.trim().length < 10}>
          Inscrire l'opposition
        </button>
      </div>
    </form>
  );
}
